import {
  EdgeLabelRenderer,
  getSmoothStepPath,
  useReactFlow,
} from '@xyflow/react';
import { useContext } from 'react';
import { NODES_TYPES } from '../types';
import { DeleteSvg } from '../svgs';
import { ThemeContext } from '../../../../../pages/_app';
import styles from '../timeline-node-manager.module.css';

export default function DeletableEdge({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  style = {},
  markerEnd = '',
  source = '',
  target = '',
  selected = false,
  highlighted = false,
  data = {},
}: any) {
  const { setEdges, setNodes, getNodes } = useReactFlow();
  const { isDark } = useContext(ThemeContext);

  const isHighlighted = highlighted || data?.highlighted || false;
  const isSelected = selected || false;

  const [edgePath, labelX, labelY] = getSmoothStepPath({
    sourceX,
    sourceY,
    targetX,
    targetY,
    sourcePosition,
    targetPosition,
  });

  const getEdgeColor = () => {
    if (isSelected) {
      return 'rgba(224, 33, 137, 1)';
    }
    if (isHighlighted) {
      return 'rgba(247, 141, 199, 1)';
    }
    return isDark ? 'rgba(102, 102, 102, 1)' : 'rgba(161, 161, 170, 1)';
  };

  const edgeColor = getEdgeColor();
  const strokeWidth = isSelected || isHighlighted ? 2.5 : 1.5;

  function onDeleteEdge() {
    const targetNode = getNodes().find((node) => node.id === target);
    const sourceNode = getNodes().find((node) => node.id === source);

    const eventLink =
      sourceNode?.type === NODES_TYPES.EVENT_LINK
        ? sourceNode
        : targetNode?.type === NODES_TYPES.EVENT_LINK
        ? targetNode
        : null;

    if (eventLink) {
      setNodes((nodes) =>
        nodes.map((n) =>
          n.id === eventLink.id
            ? {
                ...n,
                data: {
                  ...n.data,
                  [sourceNode?.id === eventLink.id
                    ? 'targetEvent'
                    : 'sourceEvent']: null,
                },
              }
            : n
        )
      );
    }

    if (targetNode?.type === NODES_TYPES.ACTION) {
      setNodes((nodes) =>
        nodes
          ? nodes.map((n) =>
              n.id === targetNode.id
                ? {
                    ...n,
                    data: {
                      ...n.data,
                      isOnComplete: undefined,
                      onCompleteConditionId: undefined,
                      eventId: undefined,
                    },
                  }
                : n
            )
          : []
      );
    }

    setEdges((es) => es.filter((e) => e.id !== id));
  }

  const arrowMarkerId = `arrow-${id}`;

  return (
    <>
      <svg style={{ position: 'absolute', width: 0, height: 0 }}>
        <defs>
          <marker
            id={arrowMarkerId}
            markerWidth='8'
            markerHeight='6'
            refX='5.5'
            refY='3'
            orient='auto'
            stroke={edgeColor}
            markerUnits='strokeWidth'
          >
            <path
              d='M3,1 L6,3 L3,5'
              fill='none'
              stroke={edgeColor}
              strokeWidth='1'
              strokeLinecap='round'
              strokeLinejoin='round'
            />
          </marker>
        </defs>
      </svg>

      <path
        id={id}
        style={{
          stroke: edgeColor,
          strokeWidth: strokeWidth,
          fill: 'none',
          cursor: 'pointer',
          transition: 'stroke 0.2s ease, stroke-width 0.2s ease',
          ...style,
        }}
        className={`react-flow__edge-path ${
          isHighlighted ? styles.highlightedEdge : ''
        } ${isSelected ? styles.selectedEdge : ''}`}
        d={edgePath}
        markerEnd={`url(#${arrowMarkerId})`}
      />
      {isSelected && (
        <EdgeLabelRenderer>
          <button
            style={{
              position: 'absolute',
              transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`,
              pointerEvents: 'all',
              background: isDark ? '#1f1f1f' : 'white',
              border: `1px solid ${isDark ? '#4c4c4c' : '#ddd'}`,
              borderRadius: '4px',
              padding: '6px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: isDark
                ? '0 2px 8px rgba(0,0,0,0.5)'
                : '0 2px 8px rgba(0,0,0,0.15)',
              zIndex: 1000,
              transition: 'all 0.2s ease',
            }}
            className='nodrag nopan'
            onClick={(e) => {
              e.stopPropagation();
              onDeleteEdge();
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = `translate(-50%, -50%) translate(${labelX}px,${labelY}px) scale(1.1)`;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = `translate(-50%, -50%) translate(${labelX}px,${labelY}px) scale(1)`;
            }}
            title='Delete edge'
          >
            {DeleteSvg}
          </button>
        </EdgeLabelRenderer>
      )}
    </>
  );
}
